import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import PaginationControls from '../common/PaginationControls';
import NoticeToast from '../common/NoticeToast';
import {
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  getAllRoles,
  createRole,
  updateRole,
  deleteRole
} from '../../api/admin';
import { useNotice } from '../../hooks/useNotice';
import './RoleManagement.css';

const USERS_ITEMS_PER_PAGE = 30;
const ROLES_ITEMS_PER_PAGE = 5;

export default function RoleManagement() {
  const location = useLocation();
  const { notice, showNotice, clearNotice } = useNotice();
  const [activeTab, setActiveTab] = useState('roles');
  
  // États pour les utilisateurs
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal utilisateur
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    nom: '',
    email: '',
    mot_de_passe: '',
    id_role: ''
  });
  
  // Modal rôle
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    code: '',
    nom: ''
  });
  
  // Recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [rolesPage, setRolesPage] = useState(1);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/admin/users')) {
      setActiveTab('users');
    } else {
      setActiveTab('roles');
    }
  }, [location.pathname]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersData, rolesData] = await Promise.all([
        getAllUsers(),
        getAllRoles()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err) {
      setError('Erreur lors du chargement des données.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ========== Gestion Utilisateurs ==========

  const handleOpenUserModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserForm({
        nom: user.nom,
        email: user.email,
        mot_de_passe: '',
        id_role: user.id_role
      });
    } else {
      setEditingUser(null);
      setUserForm({ nom: '', email: '', mot_de_passe: '', id_role: '' });
    }
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setUserModalOpen(false);
    setEditingUser(null);
    setUserForm({ nom: '', email: '', mot_de_passe: '', id_role: '' });
  };

  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    if (!userForm.nom || !userForm.email || !userForm.id_role) {
      showNotice('Veuillez remplir tous les champs obligatoires.', 'warning');
      return;
    }

    if (!editingUser && !userForm.mot_de_passe) {
      showNotice('Le mot de passe est obligatoire pour un nouvel utilisateur.', 'warning');
      return;
    }

    try {
      if (editingUser) {
        // Mise à jour
        await updateUser(editingUser.id, {
          nom: userForm.nom,
          email: userForm.email,
          id_role: parseInt(userForm.id_role),
          mot_de_passe: userForm.mot_de_passe || null
        });
        showNotice('Utilisateur mis a jour avec succes.', 'success');
      } else {
        // Création
        await createUser({
          nom: userForm.nom,
          email: userForm.email,
          mot_de_passe: userForm.mot_de_passe,
          id_role: parseInt(userForm.id_role)
        });
        showNotice('Utilisateur cree avec succes.', 'success');
      }
      handleCloseUserModal();
      loadData();
    } catch (err) {
      showNotice(err.message || 'Erreur lors de la sauvegarde de l\'utilisateur.', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      await deleteUser(userId);
      showNotice('Utilisateur supprime avec succes.', 'success');
      loadData();
    } catch (err) {
      showNotice(err.message || 'Erreur lors de la suppression de l\'utilisateur.', 'error');
    }
  };

  // ========== Gestion Rôles ==========

  const handleOpenRoleModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({ code: role.code, nom: role.nom });
    } else {
      setEditingRole(null);
      setRoleForm({ code: '', nom: '' });
    }
    setRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setEditingRole(null);
    setRoleForm({ code: '', nom: '' });
  };

  const handleRoleFormChange = (field, value) => {
    setRoleForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    
    if (!roleForm.code || !roleForm.nom) {
      showNotice('Veuillez remplir tous les champs.', 'warning');
      return;
    }

    try {
      if (editingRole) {
        await updateRole(editingRole.id, roleForm);
        showNotice('Role mis a jour avec succes.', 'success');
      } else {
        await createRole(roleForm);
        showNotice('Role cree avec succes.', 'success');
      }
      handleCloseRoleModal();
      loadData();
    } catch (err) {
      showNotice(err.message || 'Erreur lors de la sauvegarde du role.', 'error');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rôle ? Les utilisateurs associés doivent être réaffectés d\'abord.')) return;
    
    try {
      await deleteRole(roleId);
      showNotice('Role supprime avec succes.', 'success');
      loadData();
    } catch (err) {
      showNotice(err.message || 'Erreur lors de la suppression du role.', 'error');
    }
  };

  // ========== Filtres ==========

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || u.role_code === filterRole;
    return matchesSearch && matchesRole;
  });

  const filteredRoles = roles.filter(r => 
    r.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedFilteredUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const nameCompare = (a.nom || '').localeCompare(b.nom || '');
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return a.id - b.id;
    });
  }, [filteredUsers]);

  const sortedFilteredRoles = useMemo(() => {
    return [...filteredRoles].sort((a, b) => {
      const nameCompare = (a.nom || '').localeCompare(b.nom || '');
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return a.id - b.id;
    });
  }, [filteredRoles]);

  const usersTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedFilteredUsers.length / USERS_ITEMS_PER_PAGE));
  }, [sortedFilteredUsers.length]);

  const rolesTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedFilteredRoles.length / ROLES_ITEMS_PER_PAGE));
  }, [sortedFilteredRoles.length]);

  const paginatedUsers = useMemo(() => {
    const start = (usersPage - 1) * USERS_ITEMS_PER_PAGE;
    return sortedFilteredUsers.slice(start, start + USERS_ITEMS_PER_PAGE);
  }, [sortedFilteredUsers, usersPage]);

  const paginatedRoles = useMemo(() => {
    const start = (rolesPage - 1) * ROLES_ITEMS_PER_PAGE;
    return sortedFilteredRoles.slice(start, start + ROLES_ITEMS_PER_PAGE);
  }, [sortedFilteredRoles, rolesPage]);

  useEffect(() => {
    setUsersPage(1);
    setRolesPage(1);
  }, [searchTerm, filterRole, activeTab]);

  useEffect(() => {
    if (usersPage > usersTotalPages) {
      setUsersPage(usersTotalPages);
    }
  }, [usersPage, usersTotalPages]);

  useEffect(() => {
    if (rolesPage > rolesTotalPages) {
      setRolesPage(rolesTotalPages);
    }
  }, [rolesPage, rolesTotalPages]);

  const usersWithRole = useMemo(() => users.filter((user) => Boolean(user.id_role)).length, [users]);
  const populatedRoles = useMemo(() => roles.filter((role) => users.some((user) => user.id_role === role.id)).length, [roles, users]);

  const roleKpis = useMemo(() => {
    if (activeTab === 'users') {
      return [
        { label: 'Utilisateurs', value: users.length, helper: 'comptes enregistrés' },
        { label: 'Rôles disponibles', value: roles.length, helper: 'profils configurés' },
        { label: 'Utilisateurs filtrés', value: filteredUsers.length, helper: 'vue actuelle' },
        { label: 'Comptes rattachés', value: usersWithRole, helper: 'avec rôle attribué' },
      ];
    }

    return [
      { label: 'Rôles', value: roles.length, helper: 'profils disponibles' },
      { label: 'Rôles filtrés', value: filteredRoles.length, helper: 'vue actuelle' },
      { label: 'Rôles utilisés', value: populatedRoles, helper: 'au moins un utilisateur' },
      { label: 'Utilisateurs impactés', value: users.length, helper: 'comptes administrés' },
    ];
  }, [activeTab, users.length, roles.length, filteredUsers.length, filteredRoles.length, usersWithRole, populatedRoles]);

  return (
    <div className="admin-role-page">
      <AdminSidebar />

      <main className="admin-role-main">
        <header className="admin-role-header">
          <h1>Gestion des utilisateurs et rôles</h1>
          <p>Interface administrateur dédiée à la configuration des accès</p>
        </header>

        <div className="admin-role-kpi-strip">
          {roleKpis.map((kpi) => (
            <div key={kpi.label} className="admin-role-kpi-card">
              <span className="admin-role-kpi-label">{kpi.label}</span>
              <strong className="admin-role-kpi-value">{kpi.value}</strong>
              <span className="admin-role-kpi-helper">{kpi.helper}</span>
            </div>
          ))}
        </div>

        <section className="admin-role-card">
          <div className="admin-role-toolbar">
            <input
              type="text"
              placeholder={activeTab === 'users' ? 'Rechercher un utilisateur...' : 'Rechercher un rôle...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />

            {activeTab === 'users' && (
              <>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="filter-select"
                >
                  <option value="">Tous les rôles</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.code}>{r.nom}</option>
                  ))}
                </select>
                <button className="btn-primary" onClick={() => handleOpenUserModal()}>
                  + Nouvel utilisateur
                </button>
              </>
            )}

            {activeTab === 'roles' && (
              <button className="btn-primary" onClick={() => handleOpenRoleModal()}>
                + Nouveau rôle
              </button>
            )}
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <>
              {activeTab === 'users' && (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nom</th>
                        <th>Email</th>
                        <th>Rôle</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map(u => (
                        <tr key={u.id}>
                          <td>{u.id}</td>
                          <td><strong>{u.nom}</strong></td>
                          <td>{u.email}</td>
                          <td>
                            <span className={`role-badge ${u.role_code}`}>{u.role_nom}</span>
                          </td>
                          <td className="actions-cell">
                            <button className="btn-icon btn-edit" onClick={() => handleOpenUserModal(u)} title="Modifier">✏️</button>
                            <button className="btn-icon btn-delete" onClick={() => handleDeleteUser(u.id)} title="Supprimer">🗑️</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && <div className="no-data">Aucun utilisateur trouvé</div>}
                  <PaginationControls currentPage={usersPage} totalPages={usersTotalPages} onPageChange={setUsersPage} />
                </div>
              )}

              {activeTab === 'roles' && (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Code</th>
                        <th>Nom</th>
                        <th>Utilisateurs</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRoles.map(r => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td><code>{r.code}</code></td>
                          <td><strong>{r.nom}</strong></td>
                          <td>{users.filter(u => u.id_role === r.id).length}</td>
                          <td className="actions-cell">
                            <button className="btn-icon btn-edit" onClick={() => handleOpenRoleModal(r)} title="Modifier">✏️</button>
                            <button className="btn-icon btn-delete" onClick={() => handleDeleteRole(r.id)} title="Supprimer">🗑️</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredRoles.length === 0 && <div className="no-data">Aucun rôle trouvé</div>}
                  <PaginationControls currentPage={rolesPage} totalPages={rolesTotalPages} onPageChange={setRolesPage} />
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {/* Modal Utilisateur */}
      {userModalOpen && (
        <div className="modal-overlay" onClick={handleCloseUserModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h2>
              <button className="modal-close" onClick={handleCloseUserModal}>✕</button>
            </div>
            <form onSubmit={handleSaveUser} className="modal-form">
              <div className="form-group">
                <label>Nom complet *</label>
                <input
                  type="text"
                  value={userForm.nom}
                  onChange={(e) => handleUserFormChange('nom', e.target.value)}
                  placeholder="Jean Dupont"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => handleUserFormChange('email', e.target.value)}
                  placeholder="jean.dupont@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Mot de passe {editingUser && '(laisser vide pour ne pas changer)'}</label>
                <input
                  type="password"
                  value={userForm.mot_de_passe}
                  onChange={(e) => handleUserFormChange('mot_de_passe', e.target.value)}
                  placeholder={editingUser ? 'Nouveau mot de passe' : 'Mot de passe'}
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Rôle *</label>
                <select
                  value={userForm.id_role}
                  onChange={(e) => handleUserFormChange('id_role', e.target.value)}
                  required
                >
                  <option value="">-- Sélectionner un rôle --</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.nom}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseUserModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-save">
                  {editingUser ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Rôle */}
      {roleModalOpen && (
        <div className="modal-overlay" onClick={handleCloseRoleModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}</h2>
              <button className="modal-close" onClick={handleCloseRoleModal}>✕</button>
            </div>
            <form onSubmit={handleSaveRole} className="modal-form">
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={roleForm.code}
                  onChange={(e) => handleRoleFormChange('code', e.target.value)}
                  placeholder="EX: EN, CE, CP, DA"
                  maxLength="10"
                  required
                />
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={roleForm.nom}
                  onChange={(e) => handleRoleFormChange('nom', e.target.value)}
                  placeholder="Nom du rôle"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseRoleModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-save">
                  {editingRole ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <NoticeToast notice={notice} onClose={clearNotice} />
    </div>
  );
}
